import axios from 'axios';

import { apiClient } from './client';

export type TicketStatus = 'draft' | 'rejected' | 'issue_created';
export type TicketSeverity = 'low' | 'medium' | 'high';
export type TicketSource = 'auto' | 'manual' | 'judge';

export interface TicketListItem {
  id: string;
  runDate: string | null;
  source: TicketSource;
  status: TicketStatus;
  title: string;
  summary: string;
  severity: TicketSeverity;
  categories: string[];
  sessionIds: string[];
  fingerprint: string | null;
  githubIssueUrl: string | null;
  createdAt: string;
}

export interface AiTicket extends TicketListItem {
  bodyMarkdown: string;
  evidence: unknown;
  manualNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  rejectedAt: string | null;
  updatedAt: string;
}

export interface TicketRun {
  runDate: string;
  windowFrom: string;
  windowTo: string;
  status: 'running' | 'completed' | 'failed';
  sessionsScanned: number;
  sessionsAnalyzed: number;
  sessionsFlagged: number;
  ticketsCreated: number;
  skippedDuplicates: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ListTicketsParams {
  status?: TicketStatus;
  limit?: number;
  cursor?: string;
}

export interface ListTicketsResponse {
  items: TicketListItem[];
  nextCursor: string | null;
  total: number;
}

export const listTickets = async (params: ListTicketsParams = {}): Promise<ListTicketsResponse> => {
  const { data } = await apiClient.get<ListTicketsResponse>('/tickets', { params });
  return data;
};

export const getTicket = async (id: string): Promise<{ ticket: AiTicket }> => {
  const { data } = await apiClient.get<{ ticket: AiTicket }>(`/tickets/${id}`);
  return data;
};

export const approveTicket = async (
  id: string,
): Promise<{ issueUrl: string; existing?: boolean }> => {
  const { data } = await apiClient.post<{ issueUrl: string; existing?: boolean }>(
    `/tickets/${id}/approve`,
  );
  return data;
};

export const rejectTicket = async (
  id: string,
  reason?: string,
): Promise<{ id: string; status: TicketStatus }> => {
  const { data } = await apiClient.post<{ id: string; status: TicketStatus }>(
    `/tickets/${id}/reject`,
    { reason },
  );
  return data;
};

export const createTicketFromSession = async (
  sessionId: string,
  note?: string,
): Promise<{ ticket: AiTicket }> => {
  const { data } = await apiClient.post<{ ticket: AiTicket }>(
    `/tickets/from-session/${encodeURIComponent(sessionId)}`,
    { note },
  );
  return data;
};

export interface CreateTicketFromPatternInput {
  rootCause: string;
  isoYear: number;
  isoWeek: number;
  note?: string;
}

// 409 betyder att veckans mönster redan har ett utkast eller en issue. Det är ett
// svar, inte ett fel: anroparen öppnar den befintliga ticketen i stället.
export const createTicketFromPattern = async (
  input: CreateTicketFromPatternInput,
): Promise<{ ticket: AiTicket } | { duplicateOf: string }> => {
  try {
    const { data } = await apiClient.post<{ ticket: AiTicket }>('/tickets/from-pattern', input);
    return data;
  } catch (err) {
    const ticketId: unknown = axios.isAxiosError(err) ? err.response?.data?.ticketId : undefined;
    if (axios.isAxiosError(err) && err.response?.status === 409 && typeof ticketId === 'string') {
      return { duplicateOf: ticketId };
    }
    throw err;
  }
};

export const listTicketRuns = async (limit = 7): Promise<{ runs: TicketRun[] }> => {
  const { data } = await apiClient.get<{ runs: TicketRun[] }>('/tickets/runs', {
    params: { limit },
  });
  return data;
};
